
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import AuthGuard from '../../components/feature/AuthGuard';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface OrderItem {
  id: number;
  artwork_title: string;
  artwork_artist: string;
  artwork_size: string;
  artwork_price: number;
  artwork_image_url: string;
}

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  shipping_address: string;
  shipping_city: string;
  status: 'preparing' | 'shipped' | 'delivered' | 'cancelled';
  order_date: string;
  estimated_delivery: string;
  tracking_number?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  payment_method: string;
  notes: string;
  created_at: string;
  items: OrderItem[];
}

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: '',
    tracking_number: '',
    notes: '',
    estimated_delivery: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;

      // Her sipariş için ürünleri getir
      const ordersWithItems = await Promise.all(
        (ordersData || []).map(async (order) => {
          const { data: itemsData, error: itemsError } = await supabase
            .from('order_items')
            .select('*')
            .eq('order_id', order.id);

          if (itemsError) throw itemsError;

          return {
            ...order,
            items: itemsData || []
          };
        })
      );

      setOrders(ordersWithItems);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'preparing':
        return {
          text: 'Hazırlanıyor',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          icon: 'ri-time-line'
        };
      case 'shipped':
        return {
          text: 'Kargoya Verildi',
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          icon: 'ri-truck-line'
        };
      case 'delivered':
        return {
          text: 'Teslim Edildi',
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          icon: 'ri-check-line'
        };
      case 'cancelled':
        return {
          text: 'İptal Edildi',
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          icon: 'ri-close-line'
        };
      default:
        return {
          text: 'Bilinmiyor',
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          icon: 'ri-question-line'
        };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number) => {
    return `${price.toLocaleString('tr-TR')} TL`;
  };

  const openUpdateModal = (order: Order) => {
    setSelectedOrder(order);
    setUpdateData({
      status: order.status,
      tracking_number: order.tracking_number || '',
      notes: order.notes || '',
      estimated_delivery: order.estimated_delivery ? order.estimated_delivery.split('T')[0] : ''
    });
    setShowUpdateModal(true);
  };

  const handleUpdateOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrder) return;

    setIsUpdating(true);

    try {
      // Sipariş bilgilerini güncelle
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          status: updateData.status,
          tracking_number: updateData.tracking_number || null,
          notes: updateData.notes,
          estimated_delivery: updateData.estimated_delivery
        })
        .eq('id', selectedOrder.id);

      if (updateError) throw updateError;

      // Durum geçmişine kayıt ekle
      const { error: historyError } = await supabase
        .from('order_status_history')
        .insert([{
          order_id: selectedOrder.id,
          status: updateData.status,
          status_date: new Date().toISOString(),
          notes: updateData.notes || `Durum güncellendi: ${getStatusInfo(updateData.status).text}`
        }]);

      if (historyError) throw historyError;

      // Siparişleri yeniden yükle
      await fetchOrders();
      
      setShowUpdateModal(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Sipariş güncellenirken bir hata oluştu');
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filterStatus === 'all') return true;
    return order.status === filterStatus;
  });

  if (isLoading) {
    return (
      <AuthGuard requireAdmin={true}>
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard requireAdmin={true}>
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div className="flex items-center gap-8">
                <h1 className="text-3xl font-bold text-gray-900">Sipariş Yönetimi</h1>
                <nav className="flex items-center gap-6">
                  <button 
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Ana Sayfa
                  </button>
                  <button 
                    onClick={() => window.REACT_APP_NAVIGATE('/admin')}
                    className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Eser Yönetimi
                  </button>
                  <button 
                    onClick={() => window.REACT_APP_NAVIGATE('/gallery')}
                    className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Galeri
                  </button>
                </nav>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  Toplam {orders.length} sipariş
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Durum Filtresi:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filterStatus === 'all' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Tümü ({orders.length})
                </button>
                <button
                  onClick={() => setFilterStatus('preparing')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filterStatus === 'preparing' 
                      ? 'bg-yellow-600 text-white' 
                      : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                  }`}
                >
                  Hazırlanıyor ({orders.filter(o => o.status === 'preparing').length})
                </button>
                <button
                  onClick={() => setFilterStatus('shipped')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filterStatus === 'shipped' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                >
                  Kargoya Verildi ({orders.filter(o => o.status === 'shipped').length})
                </button>
                <button
                  onClick={() => setFilterStatus('delivered')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filterStatus === 'delivered' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                >
                  Teslim Edildi ({orders.filter(o => o.status === 'delivered').length})
                </button>
                <button
                  onClick={() => setFilterStatus('cancelled')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filterStatus === 'cancelled' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-red-100 text-red-600 hover:bg-red-200'
                  }`}
                >
                  İptal Edildi ({orders.filter(o => o.status === 'cancelled').length})
                </button>
              </div>
            </div>
          </div>

          {/* Orders List */}
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">#{order.order_number}</h3>
                      <div className={`px-3 py-1 rounded-full ${getStatusInfo(order.status).bgColor}`}>
                        <div className="flex items-center gap-2">
                          <i className={`${getStatusInfo(order.status).icon} ${getStatusInfo(order.status).color}`}></i>
                          <span className={`font-medium text-sm ${getStatusInfo(order.status).color}`}>
                            {getStatusInfo(order.status).text}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Müşteri:</span> {order.customer_name}
                      </div>
                      <div>
                        <span className="font-medium">Tarih:</span> {formatDate(order.created_at)}
                      </div>
                      <div>
                        <span className="font-medium">Toplam:</span> {formatPrice(order.total_amount)}
                      </div>
                      <div>
                        <span className="font-medium">E-posta:</span> {order.customer_email}
                      </div>
                      <div>
                        <span className="font-medium">Telefon:</span> {order.customer_phone}
                      </div>
                      {order.tracking_number && (
                        <div>
                          <span className="font-medium">Kargo No:</span> {order.tracking_number}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => openUpdateModal(order)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Güncelle
                  </button>
                </div>

                {/* Order Items */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Sipariş Ürünleri:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <img
                          src={item.artwork_image_url}
                          alt={item.artwork_title}
                          className="w-16 h-20 object-cover object-top rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium text-gray-900 truncate">{item.artwork_title}</h5>
                          <p className="text-sm text-gray-600">{item.artwork_artist}</p>
                          <p className="text-sm text-gray-500">{item.artwork_size}</p>
                          <p className="font-medium text-gray-900">{formatPrice(item.artwork_price)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Teslimat Adresi:</h4>
                  <p className="text-sm text-gray-600">
                    {order.shipping_address}, {order.shipping_city}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-shopping-bag-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {filterStatus === 'all' ? 'Henüz sipariş yok' : 'Bu durumda sipariş bulunamadı'}
              </h3>
              <p className="text-gray-500">
                {filterStatus === 'all' 
                  ? 'İlk siparişler geldiğinde burada görünecek' 
                  : 'Farklı bir durum filtresi deneyin'
                }
              </p>
            </div>
          )}
        </div>

        {/* Update Modal */}
        {showUpdateModal && selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Sipariş Güncelle - #{selectedOrder.order_number}</h2>
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>

                <form onSubmit={handleUpdateOrder} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sipariş Durumu
                    </label>
                    <select
                      value={updateData.status}
                      onChange={(e) => setUpdateData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg pr-8"
                      required
                    >
                      <option value="preparing">Hazırlanıyor</option>
                      <option value="shipped">Kargoya Verildi</option>
                      <option value="delivered">Teslim Edildi</option>
                      <option value="cancelled">İptal Edildi</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kargo Takip Numarası
                    </label>
                    <input
                      type="text"
                      value={updateData.tracking_number}
                      onChange={(e) => setUpdateData(prev => ({ ...prev, tracking_number: e.target.value }))}
                      placeholder="Kargo takip numarasını girin"
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Sipariş "Kargoya Verildi" durumunda ise kargo numarası girmek önemlidir
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tahmini Teslimat Tarihi
                    </label>
                    <input
                      type="date"
                      value={updateData.estimated_delivery}
                      onChange={(e) => setUpdateData(prev => ({ ...prev, estimated_delivery: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notlar
                    </label>
                    <textarea
                      value={updateData.notes}
                      onChange={(e) => setUpdateData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                      placeholder="Durum güncellemesi hakkında not ekleyin..."
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Current Order Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Mevcut Sipariş Bilgileri:</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><span className="font-medium">Müşteri:</span> {selectedOrder.customer_name}</p>
                      <p><span className="font-medium">E-posta:</span> {selectedOrder.customer_email}</p>
                      <p><span className="font-medium">Telefon:</span> {selectedOrder.customer_phone}</p>
                      <p><span className="font-medium">Adres:</span> {selectedOrder.shipping_address}, {selectedOrder.shipping_city}</p>
                      <p><span className="font-medium">Toplam:</span> {formatPrice(selectedOrder.total_amount)}</p>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      type="submit"
                      disabled={isUpdating}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {isUpdating ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Güncelleniyor...
                        </div>
                      ) : (
                        'Siparişi Güncelle'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowUpdateModal(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      İptal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthGuard>
  );
}
